<template lang="pug">
label.radio-container(
  :class="{active: modelValue === option, noHighlight: noHighlight, disabled: disabled}"
)
  input(type="radio"
    :name="name"
    :disabled="disabled"
    :value="option"
    :checked="modelValue === option"
    @input="$emit('update:modelValue', option)"
  )
  .primary
    slot
      .primary-default {{label}}
  .secondary
    slot(name="secondary")
      .secondary-default(v-if="secondary") {{secondary}}
  slot(name="bottom")
</template>
<script setup lang="ts">

type Props = {
  modelValue?: boolean | string | number | null
  name?: string
  label?: string | null
  secondary?: string | null
  option?: boolean | string | number | null
  noHighlight?: boolean
  disabled?: boolean
}

withDefaults(defineProps<Props>(), {
  modelValue  : null,
  name        : 'radio',
  label       : null,
  secondary   : null,
  option      : null,
  noHighlight : false,
  disabled    : false,
})

defineEmits<{
  (e: 'update:modelValue', value: boolean | string | number | null): void
}>()
</script>

<style lang="scss" scoped>
.radio-container {
  border: $border-width-thin solid $color-gray-light;
  border-width: $border-width-thin;
  border-bottom-color: transparent;
  cursor: pointer;
  width: 100%;
  background-color: $color-white;
  padding: $space-xs;
  align-items: center;
  display: flex;
  flex-wrap: wrap;

  &.disabled {
    opacity: $opacity-disabled;
    pointer-events: none;
  }

  &.active:not(.noHighlight) {
    background: $color-blue-light;
  }

  &:last-of-type {
    border-bottom-color: $color-gray-light;
  }

  &:focus-within {
    border-color: $color-gray-x-dark;
    border-width: $border-width-thin;
  }
}

.primary {
  flex-grow: 1;
  max-width: 90%;
}

.primary.primary-default, .secondary.secondary-default {
  font-size: $size-xs;
  font-family: $font-sans-serif;
}

input[type="radio"] {
  appearance: none;
  background-color: $color-gray-light;
  margin: 0 10px 0 0;
  color: currentColor;
  height: 10px;
  width: 10px;
  border-radius: $radius-circle;
  outline: 1px solid $color-gray;
  outline-offset: 2px;

  &:hover {
    background-color: $color-gray;
  }

  &:active {
    background-color: $color-gray;
  }

  &:checked{
    background-color: $color-blue;
    outline-color: $color-blue;
  }
}

.radio-container.active input[type="radio"] {
  background-color: $color-blue;
  outline-color: $color-blue;
}

</style>
